import { IconButton, Typography } from "@mui/material";
import { FaChevronLeft } from "react-icons/fa6";
import { Link, useNavigate } from "react-router-dom";

import { Container } from "../../common/Container/Container";
import { BottomNavigator } from "./components/ButtonNavigator";
import { LeftMenu } from "./components/LeftMenu";

const knowledgeList = [
	"Aerodynamics/Applied Aerodynamics",
	"Aircraft Systems",
	"Aircraft Instruments",
	"Weather",
	"Operational Environment (conditions or circumstances one might encounter)",
	"Performance",
	"Regulations",
	"Human Factors",
];

const skillsList = [
	"Stick & Rudder",
	"Maneuvers",
	"Risk Management",
	"Planning",
	"Navigation",
	"Communication",
	"Instrument/Weather Flying",
	"Night Flying",
];

const initialPilotTrainingList = [
	"Tutoring for Aeronautical Knowledge Tests",
	"Sport Pilot",
	"Private Airplane",
	"Private Glider Add-on Rating",
	"Private Glider",
	"Private Helicopter",
	"Recreational Helicopter",
	"Commercial Airplane",
	"Commercial Glider Add-on Rating",
	"Commercial Helicopter",
	"Flight Instructor Certificate",
	"Multi-engine Add-on to Private/Commercial",
	"Multi-engine Add-on to Flight Instructor Certificates",
	"Instrument Rating Add-on to Private/Commercial",
	"Instrument Rating Add-on to Flight Instructor Certificates",
	"Airline Transport Pilot Course",
];

const specialtyTrainingList = [
	"Seaplane Add-on",
	"CFI Spin Training",
	"Tailwheel Endorsements",
	"Basic Aerobatics",
	"Advanced Aerobatics",
	"Formation Flying",
	"Aircraft/Avionics-Specific Training",
	"Mountain/Backcountry Flying",
	"Cross-country Soaring",
];

export function FindingTheTrainingServicesThatHelpYouThrive() {
	const navigate = useNavigate();

	return (
		<Container navbarOrkaProps={{ white: true }} noFooter>
			<div className="resource-detail-container">
				<LeftMenu />
				<div className="main-content">
					<div className="resource-header">
						<IconButton
							className="icon-button back-button"
							onClick={() => {
								navigate("/resources");
							}}>
							<FaChevronLeft />
						</IconButton>
						<Typography className="normal-text">Back to Resources</Typography>
					</div>

					<Typography
						className="title-text bold resource-title"
						textAlign="center"
						id="introduction">
						Finding the training services that help you thrive
					</Typography>

					<Typography className="normal-text paragraph-top">
						Building something of your own that you can take with you wherever
						you go, owning your time, fueling your passion for aviation, and
						helping people grow... You have the freedom to start building, and
						create an aviation life that keeps your spark going.
					</Typography>

					<Typography className="normal-text paragraph-top">
						The purpose of this article is to help you recognize your strengths,
						identify the unique value you can bring to students and the aviation
						community, and give you tools to make the most of it all.
					</Typography>

					<div className="section-top">
						<Typography
							className="small-title-text bold black-text underline-on-hover-link"
							component="a"
							id="step-1-start-with-possibility"
							href="#step-1-start-with-possibility">
							Step 1: Start with Possibility
						</Typography>
					</div>

					<Typography className="normal-text first-paragraph-top">
						Forget about competition, market demand, or finding the most
						lucrative pathway. Take your doubts and toss them aside. Grab a
						blank piece of paper.
					</Typography>

					<Typography className="normal-text paragraph-top">
						Now, take a minute to reflect on your aeronautical knowledge and
						skills, subject by subject. Note what you've mastered and the
						subjects where you can enrich others with your unique experience.
						Like a tree, you'll find that your branches extend farther than you
						might currently think.
					</Typography>

					<Typography className="normal-text paragraph-top">
						The following list is universal enough to get your ideas flowing:
					</Typography>

					<Typography className="normal-text bold paragraph-top">
						Knowledge:
					</Typography>
					{knowledgeList.map((item) => (
						<div key={item} className="list-item">
							<div className="dot" />
							<Typography className="normal-text" flex={1}>
								{item}
							</Typography>
						</div>
					))}

					<Typography className="normal-text bold paragraph-top">
						Skills:
					</Typography>
					{skillsList.map((item) => (
						<div key={item} className="list-item">
							<div className="dot" />
							<Typography className="normal-text" flex={1}>
								{item}
							</Typography>
						</div>
					))}

					<Typography className="normal-text paragraph-top">
						Looking at your tree branches, notice what moves you. As you review
						your aeronautical knowledge and skills, notice when your brain fires
						off ideas on subjects. Highlight the areas that bring you
						confidence, more ideas, or simply make you smile. These are fertile
						grounds. Like a farmer, you want to plant your seeds in good soil to
						expect any harvest or reward. In any case, if you want to excel, a
						strong foundation is essential.
					</Typography>

					<Typography className="normal-text paragraph-top">
						From the things you've highlighted, identify those that you can
						transfer to your students with:
					</Typography>
					<div className="list-item">
						<div className="dot" />
						<Typography className="normal-text" flex={1}>
							The confidence that you will guide them well, stemming from your
							full understanding of the principles and the subject matter.
						</Typography>
					</div>
					<div className="list-item">
						<div className="dot" />
						<Typography className="normal-text" flex={1}>
							The safety that helps them expand their own envelope, coming from
							your ability to mitigate risk.
						</Typography>
					</div>
					<div className="list-item">
						<div className="dot" />
						<Typography className="normal-text" flex={1}>
							The energy to show up and be active with your students, driven by
							your genuine interest in the subject and the skill set you're set
							to transfer.
						</Typography>
					</div>

					<Typography className="normal-text paragraph-top">
						Those areas that are highlighted but don't meet the criteria of
						confidence, safety, and energy may still bring you satisfaction in
						your personal development and allow you to unlock those
						possibilities in the future.
					</Typography>

					<Typography className="normal-text paragraph-top">
						At this point, you know two things:
					</Typography>
					<div className="list-item">
						<Typography
							className="normal-text"
							width={{ xs: "22px", md: "24px" }}>
							1.
						</Typography>
						<Typography className="normal-text" flex={1}>
							The energy to show up and be active with your students, driven by
							your genuine interest in the subject and the skill set you're set
							to transfer.
						</Typography>
					</div>
					<div className="list-item">
						<Typography
							className="normal-text"
							width={{ xs: "22px", md: "24px" }}>
							2.
						</Typography>
						<Typography className="normal-text" flex={1}>
							The areas where you'll benefit from continuous learning.
						</Typography>
					</div>

					<div className="section-top">
						<Typography
							className="small-title-text bold black-text underline-on-hover-link"
							component="a"
							id="step-2-take-responsibility"
							href="#step-2-take-responsibility">
							Step 2: Take Responsibility
						</Typography>
					</div>

					<Typography className="normal-text">
						Travel back in time to before you started flying. Whatever you
						imagined you could do, there was always a possibility for it. The
						mystery lies in the fact that good instructors are the enablers of
						those possibilities and the happiness that unfolds as you grow to
						achieve your dreams. That piece of plastic that says "Certified
						Flight Instructor" is a certificate to grant dreams and a
						responsibility to protect the safety of our communities. Your
						students, vulnerable by nature, need both possibility and a safety
						net to reach their maximum potential. Without good instructors
						actively teaching, dreams die, and immense harm can unfold.
					</Typography>

					<Typography className="normal-text paragraph-top">
						At this point, you know whether you're ready to take full
						responsibility for your fellow aviation family members, or if, for
						any reason, you're not ready to take on that responsibility.
					</Typography>

					<div className="section-top">
						<Typography
							className="small-title-text bold black-text underline-on-hover-link"
							component="a"
							id="step-3-find-your-style"
							href="#step-3-find-your-style">
							Step 3: Find Your Style
						</Typography>
					</div>

					<Typography className="normal-text first-paragraph-top">
						Take this step without setting limitations for yourself.
					</Typography>

					<Typography className="normal-text paragraph-top">
						Now that you know what you can help students with, it's time to
						explore how. Looking at your shortlist, ask yourself, "When is the
						best time for a pilot to learn what I can teach?"
					</Typography>

					<Typography className="normal-text paragraph-top">
						The pilot's journey can start in different ways and take different
						shapes. Here are 2 lists of programs to help you organize the
						knowledge and skills you can transfer:
					</Typography>

					<Typography className="normal-text bold paragraph-top">
						Initial Pilot Training:
					</Typography>
					{initialPilotTrainingList.map((item) => (
						<div key={item} className="list-item">
							<div className="dot" />
							<Typography className="normal-text" flex={1}>
								{item}
							</Typography>
						</div>
					))}

					<Typography className="normal-text bold paragraph-top">
						Specialty Training:
					</Typography>
					{specialtyTrainingList.map((item) => (
						<div key={item} className="list-item">
							<div className="dot" />
							<Typography className="normal-text" flex={1}>
								{item}
							</Typography>
						</div>
					))}

					<Typography className="normal-text paragraph-top">
						What you've got? In the initial pilot training, students need help
						obtaining their initial certifications, whereas specialty training
						is sought by students who want or need to enhance their skills.
					</Typography>

					<Typography className="normal-text paragraph-top">
						Now you understand when you come into a student's journey, which
						sets much of the context in which your transfer of knowledge and
						skills is relevant to students.
					</Typography>

					<div className="section-top">
						<Typography
							className="small-title-text bold black-text underline-on-hover-link"
							component="a"
							id="step-4-define-your-services-and-test"
							href="#step-4-define-your-services-and-test">
							Step 4: Define Your Services and Test
						</Typography>
					</div>

					<Typography className="normal-text first-paragraph-top">
						No airplane? No problem. No office? No problem. What's great about
						this step is that it integrates Steps 1-3 in a way that makes sense
						to both you and your students.
					</Typography>

					<Typography className="normal-text paragraph-top">
						ORKA helps you introduce yourself and craft each of your services
						with a built-in learning management system, guiding you to think
						about all aspects of your service to consolidate a solution you can
						offer to students a solution students can understand. Check out{" "}
						<Link to="/resources/create-your-profile">Create your profile</Link>{" "}
						and{" "}
						<Link to="/resources/how-to-deliver-a-great-service">
							How to Deliver a great service?
						</Link>
					</Typography>

					<Typography className="normal-text paragraph-top">
						What follows: the benefits of a website for free. Get unique landing
						pages to promote your skills and each of your services, which we
						make accessible to students looking for such training.
					</Typography>

					<Typography className="normal-text paragraph-top">
						And if you're not tech-savvy, the team is happy to work with you.
						Reach out at hello@flyorka.com
					</Typography>

					<Typography className="normal-text paragraph-top">
						Armed with links built to make your value obvious, you can share
						them to educate everyone about how can you help students. You can
						also share them anytime you cross paths with someone you can help.
						In the background, ORKA will also be working to guide best-fit
						students towards you, for free.
					</Typography>

					<Typography className="normal-text paragraph-top">
						After working on this step, you clearly understand the scope of what
						you can do for students, and students are able to understand what
						you can enable them to do.
					</Typography>

					<div className="section-top">
						<Typography
							className="small-title-text bold black-text underline-on-hover-link"
							component="a"
							id="step-5-learn-and-improve"
							href="#step-5-learn-and-improve">
							Step 5: Learn and Improve
						</Typography>
					</div>

					<Typography className="normal-text first-paragraph-top">
						We started with good soil, decided to take care of the crop, picked
						seeds, planted them, and now we must pay attention. If you picked
						any seeds from the programs listed above, we all know that students
						are willing to pay for them. But where are your best-fit students?
					</Typography>

					<Typography className="normal-text paragraph-top">
						If you understand the specific value you deliver and can articulate
						it in a way that helps students determine whether your training is
						right for them, you're on the right track. From this understanding,
						you'll realize who the students are that care to train with you, and
						from that, you'll learn more about where they are. ORKA will also be
						doing this work for you in the background, helping best-fit students
						find you. In the process, both you and ORKA will learn more about
						where your best-fit students are and where the support or resistance
						lies.
					</Typography>

					<Typography className="normal-text paragraph-top">
						Take note and adjust your services every time you learn something
						new about your students. Above all, know that if you're truly
						helping students achieve what they want, your crop will bring
						rewards to you.
					</Typography>

					<BottomNavigator />
				</div>
				<div className="right-menu">
					<Typography className="big-text bold">Table of Contents</Typography>
					<div className="paragraph-top">
						<Typography
							component="a"
							href="#introduction"
							className="normal-text black-text underline-on-hover-link">
							Introduction
						</Typography>
					</div>
					<div>
						<Typography
							component="a"
							href="#step-1-start-with-possibility"
							className="normal-text black-text underline-on-hover-link">
							Step 1: Start with Possibility
						</Typography>
					</div>
					<div>
						<Typography
							component="a"
							href="#step-2-take-responsibility"
							className="normal-text black-text underline-on-hover-link">
							Step 2: Take Responsibility
						</Typography>
					</div>
					<div>
						<Typography
							component="a"
							href="#step-3-find-your-style"
							className="normal-text black-text underline-on-hover-link">
							Step 3: Find Your Style
						</Typography>
					</div>
					<div>
						<Typography
							component="a"
							href="#step-4-define-your-services-and-test"
							className="normal-text black-text underline-on-hover-link">
							Step 4: Define Your Services and Test
						</Typography>
					</div>
					<div>
						<Typography
							component="a"
							href="#step-5-learn-and-improve"
							className="normal-text black-text spacing underline-on-hover-link">
							Step 5: Learn and Improve
						</Typography>
					</div>
				</div>
			</div>
		</Container>
	);
}
